"use client";
import { IProducts } from "@/api/AppApi/services/home/interfaces/IProduct";
import ProductEmblaSlider from "./ProductEmblaSlider";
import ProductBox from "../product-box/ProductBox";
import { ITheme } from "@/api/AppApi/services/company/interfaces/ICompany";
import { getProductBoxDefaultDimAndSpacing, processProductsData } from "../common/functions";
import { latestViewedProductsAtom } from "@/hooks/productAtoms";
import { useCacheInvalidation } from "@/hooks/useCacheInvalidation";
import { useAtom } from "jotai";
import { useMemo } from "react";
interface Props {
  products: IProducts;
  setIslastProduct?: (isLastProduct: boolean) => void;
  scrollableTabs?: boolean;
  theme: ITheme;
  showCustomEventData?: boolean;
  id?: string | number;
  /** Server-generated shuffle seed — keeps the random order identical across SSR and hydration. */
  shuffleSeed?: number;
}

const ProductCarousel: React.FC<Props> = ({
  products,
  setIslastProduct,
  scrollableTabs,
  theme,
  showCustomEventData,
  id,
  shuffleSeed,
}) => {
  useCacheInvalidation();
  const [latestViewed] = useAtom(latestViewedProductsAtom);

  const mergedProducts = useMemo(() => {
    return processProductsData(products, latestViewed, 25, shuffleSeed);
  }, [products, latestViewed, shuffleSeed]);

  const size = products.configurations?.style_type!;
  const { height } = getProductBoxDefaultDimAndSpacing(size);

  return (
    // A plain height floor, NOT `content-visibility: auto`. Skipping an off-screen rail
    // saved little — the cards inside already lazy-mount — and it made the whole rail's
    // height depend on `contain-intrinsic-size` being honoured, which on some iOS/WebKit
    // builds it is not (see the note in ProductCard.tsx). A rail that stays skipped and
    // un-sized is a blank band on the home page.
    <div style={{ minHeight: height }}>
      <ProductEmblaSlider
        products={mergedProducts}
        setIslastProduct={setIslastProduct}
        id={id}
      >
        {mergedProducts?.data.map((single, index) => (
          <ProductBox
            isGrid={false}
            theme={theme}
            key={single.id}
            size={mergedProducts.configurations?.style_type!}
            {...single}
            scrollableTabs={scrollableTabs!}
            priority={index < 2}
            Customize={mergedProducts?.configurations?.customize}
            showCustomEventData={showCustomEventData}
          />
        ))}
      </ProductEmblaSlider>
    </div>
  );
};

export default ProductCarousel;
